import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ProfileSelectorComponent } from '@app/components/profile/profile-selector/profile-selector.component';
import { QualificationSummaryDialogComponent } from '@app/components/profile/qualification-summary-dialog/qualification-summary-dialog.component';
import { AuthStore } from '@app/stores/auth.store';
import { NgbCollapseModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs';

export type NaturalPersonMenu =
  | 'personal-info'
  | 'investments'
  | 'investments.accepted'
  | 'investments.pending'
  | 'investments.rejected'
  | 'watched-projects'
  | 'support-request'
  | 'message';

@Component({
  selector: 'app-natural-person-profile',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NgbCollapseModule,
    ProfileSelectorComponent,
    RouterOutlet,
  ],
  templateUrl: './natural-person-profile.component.html',
  styleUrl: './natural-person-profile.component.scss',
})
export class NaturalPersonProfileComponent implements OnInit {
  protected authStore = inject(AuthStore);
  private modalService = inject(NgbModal);
  protected selectedMenu = signal<NaturalPersonMenu>('personal-info');

  public isInvestmentsCollapsed = signal(true);

  constructor(private router: Router, private destroyRef: DestroyRef) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        const url = (event as NavigationEnd).urlAfterRedirects;
        const urlSegments = url.split('/');
        const lastSegment = urlSegments[urlSegments.length - 1] as NaturalPersonMenu;
        if (this.isValidMenuType(lastSegment)) {
          this.selectedMenu.set(lastSegment);
        }
      });
    this.setInitialCollapseState();
    effect(() => {
      this.setInitialCollapseState();
    });
  }

  ngOnInit(): void {
    this.updateMenuFromCurrentUrl();
  }

  private setInitialCollapseState(): void {
    const menu = this.selectedMenu();
    const menuGroup = menu.split('.')[0];
    this.isInvestmentsCollapsed.set(menuGroup !== 'investments');
  }

  navigateToQualification() {
    this.router.navigate(['investor-qualification-test']);
  }

  isProfessionalClient(): boolean {
    return this.authStore.currentUser()?.represents === 2;
  }

  hasQualification(): boolean {
    return this.authStore.currentUser()?.stepInvestorQualification === true;
  }

  onQualificationButtonClick(): void {
    if (this.isProfessionalClient()) {
      return;
    }

    if (!this.hasQualification()) {
      this.navigateToQualification();
      return;
    }

    const dialogRef = this.modalService.open(QualificationSummaryDialogComponent, {
      centered: true,
      size: 'lg',
      scrollable: true,
    });
    dialogRef.componentInstance.user = this.authStore.currentUser() ?? null;

    dialogRef.result.then(
      (result) => {
        if (result === 'requalify') {
          this.navigateToQualification();
        }
      },
      () => undefined,
    );
  }

  selectMenu(menu: NaturalPersonMenu) {
    this.router.navigate(['profile', 'natural', menu]);
  }

  toggleCollapse(menuGroup: 'investments'): void {
    if (menuGroup === 'investments') {
      this.isInvestmentsCollapsed.update((v) => !v);
    }
  }

  private updateMenuFromCurrentUrl(): void {
    const urlSegments = this.router.url.split('/');
    const lastSegment = urlSegments[urlSegments.length - 1] as NaturalPersonMenu;
    if (this.isValidMenuType(lastSegment)) {
      this.selectedMenu.set(lastSegment);
    }
  }

  private isValidMenuType(value: string): value is NaturalPersonMenu {
    return [
      'personal-info',
      'investments',
      'investments.accepted',
      'investments.pending',
      'investments.rejected',
      'watched-projects',
      'support-request',
      'message',
    ].includes(value);
  }
}
